import type { ColDef, ColGroupDef } from "ag-grid-enterprise";
import { HeaderLabelRenderer } from "./header_label_renderer";

/**
 * Recursively defaults unset `headerTooltip`s to `headerName`, gated on truncation via
 * {@link HeaderLabelRenderer}. Groups stay ungated: AG Grid has no group inner-header hook.
 * Mirrored 1:1 by the web component's `data-grid-columns.builders.ts` — change both.
 *
 * @param columnDefs - The column definitions to default tooltips on.
 * @param defaultColDef - The resolved `defaultColDef`; a grid-wide header customization or tooltip skips every column.
 */
export function withDefaultHeaderTooltips(
  columnDefs: (ColDef | ColGroupDef)[],
  defaultColDef?: ColDef
): (ColDef | ColGroupDef)[] {
  if (
    defaultColDef?.headerComponent ||
    defaultColDef?.headerComponentParams ||
    defaultColDef?.headerTooltip !== undefined
  ) {
    return columnDefs;
  }
  return mapHeaderTooltips(columnDefs);
}

function mapHeaderTooltips(
  columnDefs: (ColDef | ColGroupDef)[]
): (ColDef | ColGroupDef)[] {
  return columnDefs.map((colDef) => {
    if ("children" in colDef && colDef.children) {
      return {
        ...withDefaultGroupHeaderTooltip(colDef),
        children: mapHeaderTooltips(colDef.children),
      };
    }
    return withGatedHeaderTooltip(colDef);
  });
}

function withDefaultGroupHeaderTooltip(colDef: ColGroupDef): ColGroupDef {
  return colDef.headerName && colDef.headerTooltip === undefined
    ? { ...colDef, headerTooltip: colDef.headerName }
    : colDef;
}

function withGatedHeaderTooltip(colDef: ColDef): ColDef {
  const canDefaultHeaderTooltip =
    colDef.headerName &&
    colDef.headerTooltip === undefined &&
    !colDef.headerComponent &&
    !colDef.headerComponentParams;
  if (!canDefaultHeaderTooltip) return colDef;

  return {
    ...colDef,
    headerTooltip: colDef.headerName,
    headerComponentParams: {
      innerHeaderComponent: HeaderLabelRenderer,
    },
  };
}
